import { FC, useCallback, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";

import useMarketplaceList from "sections/marketplacePage/useMarketplaceList";

import Loader from "components/Loader";
import NoItems from "components/NoItems";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import StoreTile from "components/StoreTile";
import CustomSearch from "components/CustomSearch";

import { Client } from "services/store/Store.interface";

const MarketplaceSection: FC = () => {
  const [filter, setFilter] = useState<Record<string, string>>();
  const { items, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useMarketplaceList({
      limit: 8,
      additionalFilters: filter,
    });

  const onSearch = useCallback((value: string) => {
    if (value.length >= 2) {
      setFilter({ "filters[search]": value });
    } else {
      setFilter(undefined);
    }
  }, []);

  useEffect(() => {
    window.history.scrollRestoration = "manual";
  }, []);

  const wrapperEl = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!wrapperEl.current || !hasNextPage || isFetching) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchNextPage();
      }
    });
    observer.observe(wrapperEl.current);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetching]);

  return (
    <>
      <Container
        sx={{
          flex: 1,
          position: "relative",
        }}
      >
        <Box my={3} display="flex" alignItems="center" flexDirection="column">
          <Box
            sx={{
              width: {
                xs: "100%",
                md: "50%",
              },
              textAlign: "center",
            }}
          >
            <CustomSearch
              placeholder="SEARCH ITEMS"
              onSearch={onSearch}
              defaultValue=""
            />
          </Box>
        </Box>
      </Container>
      <Box width="100%" py={3}>
        {isLoading && (
          <GridContainer>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((_, id) => (
              <GridItem xs={12} sm={4} md={3} key={`item-${id}`}>
                <StoreTile
                  client={{ name: "\u00A0" } as Client}
                  showSoldOut={false}
                />
              </GridItem>
            ))}
            <GridItem xs={12} mt={4}>
              <Loader />
            </GridItem>
          </GridContainer>
        )}
        {!isLoading && items.length === 0 && (
          <NoItems>
            <div>
              <Typography variant="h2" gutterBottom>
                Sorry, there are no collections with that name
              </Typography>
              <Typography>Change your search criteria</Typography>
            </div>
          </NoItems>
        )}
        <GridContainer>
          {items.map((store, id) => (
            <GridItem xs={12} sm={4} md={3} key={`item-${id}`}>
              <StoreTile
                client={store}
                isOpenseaRedirect
                showSoldOut={false}
                showTimer={false}
              />
            </GridItem>
          ))}
          {isFetching && (
            <GridItem xs={12} mt={6}>
              <Loader />
            </GridItem>
          )}
        </GridContainer>
        <Box ref={wrapperEl} />
      </Box>
    </>
  );
};

export default MarketplaceSection;
